import { db, deadLettersCollection } from "@repo/ui";
import { limit, onSnapshot, query, where } from "firebase/firestore";
import { AlertTriangle, ArrowRight, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";

interface DeadLetterNoticeProps {
	onNavigate: (page: string) => void;
}

export function DeadLetterNotice({ onNavigate }: DeadLetterNoticeProps) {
	const [hasPending, setHasPending] = useState(false);
	const [loadFailed, setLoadFailed] = useState(false);

	useEffect(() => {
		const pendingQuery = query(
			deadLettersCollection(db),
			where("status", "==", "pending"),
			limit(1),
		);
		return onSnapshot(
			pendingQuery,
			(snapshot) => {
				setHasPending(!snapshot.empty);
				setLoadFailed(false);
			},
			(error) => {
				console.error("Failed to monitor pending dead letters", error);
				setLoadFailed(true);
			},
		);
	}, []);

	if (!loadFailed && !hasPending) return null;

	return (
		<div
			className={
				loadFailed
					? "flex flex-col gap-3 border-b border-red-200 bg-red-50 px-4 py-3 text-red-950 sm:flex-row sm:items-center sm:justify-between md:px-8"
					: "flex flex-col gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 sm:flex-row sm:items-center sm:justify-between md:px-8"
			}
			role="status"
			aria-live="polite"
		>
			<div className="flex min-w-0 items-start gap-3">
				{loadFailed ? (
					<ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
				) : (
					<AlertTriangle
						className="mt-0.5 h-5 w-5 shrink-0"
						aria-hidden="true"
					/>
				)}
				<div>
					<p className="text-sm font-semibold">
						{loadFailed
							? "Failed-event monitoring is unavailable"
							: "Failed events need manual review"}
					</p>
					<p className="mt-0.5 text-sm opacity-80">
						{loadFailed
							? "The admin app could not verify whether pending dead letters exist."
							: "Automated retries have stopped for these events."}
					</p>
				</div>
			</div>
			<Button
				variant="outline"
				size="sm"
				className="w-fit border-current bg-white/70 hover:bg-white"
				onClick={() => onNavigate("dead-letters")}
			>
				Review failed events
				<ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
			</Button>
		</div>
	);
}
